import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Globe, Users, Briefcase, TrendingUp, Trophy } from 'lucide-react';
import { CrmShell } from './CrmShell';
import { Badge, Avatar, KpiCard, ActivityTimeline, PropRow, EmptyState } from './ui';
import {
  companyById, contactsByCompany, dealsByCompany, activitiesByCompany,
  contactFullName, CONTACT_STATUS, STAGE_TONE, stageByKey, formatEur, formatDateFr,
} from './mockData';

export default function CrmCompany({ plugins }) {
  const { id } = useParams();
  const company = companyById(id);

  if (!company) {
    return (
      <CrmShell plugins={plugins}>
        <EmptyState title="Société introuvable" message="Cette fiche société de démonstration n'existe pas.">
          <Link to="/crm" className="btn btn-secondary">Retour au CRM</Link>
        </EmptyState>
      </CrmShell>
    );
  }

  const cs = contactsByCompany(company.id);
  const cd = dealsByCompany(company.id);
  const acts = activitiesByCompany[company.id] || [];
  const st = CONTACT_STATUS[company.status] || CONTACT_STATUS.lead;
  const pipeline = cd.filter((d) => d.stage !== 'gagne').reduce((n, d) => n + d.amount, 0);
  const won = cd.filter((d) => d.stage === 'gagne').reduce((n, d) => n + d.amount, 0);

  return (
    <CrmShell plugins={plugins}>
      <Link to="/crm" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-iris-700 transition-colors mb-4">
        <ArrowLeft size={16} /> CRM
      </Link>

      {/* Bandeau en-tête */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 mb-6">
        <div className="flex items-start gap-4">
          <span className="w-14 h-14 flex-none rounded-2xl bg-slate-100 flex items-center justify-center text-3xl" aria-hidden="true">{company.emoji}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="h-page">{company.name}</h1>
              <Badge tone={st.tone} dot>{st.label}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-500">{company.sector} · {company.headcount} collaborateurs</p>
          </div>
          <div className="hidden sm:flex flex-col items-end gap-1 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-500"><Globe size={14} /> {company.website}</span>
            <span className="text-slate-400 text-xs">Responsable : {company.owner}</span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Transactions" value={cd.length} tone="default" icon={<Briefcase size={16} />} />
        <KpiCard label="Pipeline ouvert" value={formatEur(pipeline)} tone="primary" icon={<TrendingUp size={16} />} />
        <KpiCard label="Gagné" value={formatEur(won)} tone="success" icon={<Trophy size={16} />} />
        <KpiCard label="Contacts" value={cs.length} tone="default" icon={<Users size={16} />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne principale */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <h2 className="px-5 py-3.5 border-b border-slate-200 font-semibold text-slate-900">Transactions</h2>
            <ul className="divide-y divide-slate-100">
              {cd.map((d) => {
                const stg = stageByKey(d.stage);
                return (
                  <li key={d.id}>
                    <Link to={`/crm/affaire/${d.id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 transition-colors">
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900 truncate">{d.name}</span>
                        <span className="block text-xs text-slate-400">Échéance {formatDateFr(d.dueDate)}</span>
                      </span>
                      <span className="flex items-center gap-3 flex-none">
                        <Badge tone={STAGE_TONE[d.stage]}>{stg?.title}</Badge>
                        <span className="font-display text-sm font-bold text-slate-900 tabular-nums">{formatEur(d.amount)}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
              {cd.length === 0 && <li className="px-5 py-8 text-center text-sm text-slate-400">Aucune transaction.</li>}
            </ul>
          </section>

          <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h2 className="font-semibold text-slate-900 mb-4">Activité</h2>
            <ActivityTimeline items={acts} />
          </section>
        </div>

        {/* Rail : contacts + propriétés */}
        <div className="flex flex-col gap-6">
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <h2 className="px-5 py-3.5 border-b border-slate-200 font-semibold text-slate-900">Contacts</h2>
            <ul className="divide-y divide-slate-100">
              {cs.map((c) => {
                const cst = CONTACT_STATUS[c.status] || CONTACT_STATUS.lead;
                return (
                  <li key={c.id}>
                    <Link to={`/crm/contact/${c.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors">
                      <Avatar name={contactFullName(c)} size="sm" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-900 truncate">{contactFullName(c)}</span>
                        <span className="block text-xs text-slate-400 truncate">{c.role}</span>
                      </span>
                      {c.primary && <Badge tone="primary">Principal</Badge>}
                      <span className="sr-only">{cst.label}</span>
                    </Link>
                  </li>
                );
              })}
              {cs.length === 0 && <li className="px-5 py-8 text-center text-sm text-slate-400">Aucun contact.</li>}
            </ul>
          </section>

          <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col gap-4">
            <h2 className="font-semibold text-slate-900">Propriétés</h2>
            <PropRow label="Secteur">{company.sector}</PropRow>
            <PropRow label="Effectif">{company.headcount}</PropRow>
            <PropRow label="Site web">
              <a href={`https://${company.website}`} target="_blank" rel="noreferrer" className="text-iris-700 hover:underline">{company.website}</a>
            </PropRow>
            <PropRow label="Responsable">{company.owner}</PropRow>
            <PropRow label="Client depuis">{company.clientSince ? formatDateFr(company.clientSince) : '—'}</PropRow>
          </section>
        </div>
      </div>
    </CrmShell>
  );
}
